/**
 * Billing Service
 *
 * Handles billing and payment API calls:
 * - Account balance
 * - Transactions
 * - Invoices
 * - Payments
 * - Payment methods
 * - Stripe integration
 */

import { api } from '@/lib/api';
import type {
  Balance,
  Transaction,
  TransactionListParams,
  BalanceHistory,
  AutoReloadConfig,
  Invoice,
  InvoiceListParams,
  PaymentMethod,
  DepositIntentResponse,
  DepositConfirmRequest,
  DepositConfirmResponse,
  AddPaymentMethodRequest,
  BillingAddress,
  PayInvoiceRequest,
} from '@/types/billing';

/**
 * Billing Service Methods
 */
export const billingService = {
  /**
   * Get current balance
   */
  getBalance: (): Promise<Balance> => {
    return api.get('/billing/balance');
  },

  /**
   * Get transactions with filtering and pagination
   */
  getTransactions: async (params?: TransactionListParams): Promise<{
    transactions: Transaction[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await api.getList<Transaction>('/billing/transactions', { params });
    return {
      transactions: response.data,
      total: response.pagination.totalItems,
      page: response.pagination.currentPage,
      limit: response.pagination.itemsPerPage
    };
  },

  /**
   * Get balance history for chart
   */
  getBalanceHistory: (days: number = 30): Promise<BalanceHistory[]> => {
    return api.get('/billing/balance/history', { params: { days } });
  },

  /**
   * Configure auto-reload settings
   */
  configureAutoReload: (config: AutoReloadConfig): Promise<AutoReloadConfig> => {
    return api.post('/billing/auto-reload', config);
  },

  /**
   * Get auto-reload configuration
   */
  getAutoReloadConfig: (): Promise<AutoReloadConfig> => {
    return api.get('/billing/auto-reload');
  },

  /**
   * Export transactions to CSV
   */
  exportTransactions: (params?: TransactionListParams): Promise<Blob> => {
    return api.get('/billing/transactions/export', {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    });
  },

  /**
   * Create Stripe PaymentIntent for deposit / one-time payment.
   * Uses the Stripe payments endpoint for creating payment intents.
   */
  createDepositIntent: (amount: number, currency: string = 'USD', orderId?: string): Promise<DepositIntentResponse> => {
    return api.post('/stripe/payments/create-intent', { amount, currency, orderId });
  },

  /**
   * Confirm deposit after successful Stripe payment.
   * Uses the Stripe payments confirm endpoint.
   */
  confirmDeposit: (data: DepositConfirmRequest): Promise<DepositConfirmResponse> => {
    return api.post('/stripe/payments/confirm', {
      paymentIntentId: data.paymentIntentId,
      orderId: data.orderId,
    });
  },

  /**
   * Create Stripe SetupIntent for adding payment method.
   * Returns a client secret for use with Stripe Elements confirmCardSetup.
   */
  createSetupIntent: (): Promise<{ clientSecret: string }> => {
    return api.post('/stripe/payments/create-setup-intent');
  },

  /**
   * List invoices with filtering and pagination
   */
  getInvoices: async (params?: InvoiceListParams): Promise<{
    invoices: Invoice[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await api.getList<Invoice>('/billing/invoices', { params });
    return {
      invoices: response.data,
      total: response.pagination.totalItems,
      page: response.pagination.currentPage,
      limit: response.pagination.itemsPerPage
    };
  },

  /**
   * Get invoice by ID
   */
  getInvoiceById: (id: string): Promise<Invoice> => {
    return api.get(`/billing/invoices/${id}`);
  },

  /**
   * Download invoice PDF
   */
  downloadInvoicePDF: (id: string): Promise<Blob> => {
    return api.get(`/billing/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
  },

  /**
   * Pay an invoice
   */
  payInvoice: (id: string, data: PayInvoiceRequest): Promise<{
    success: boolean;
    transactionId: string;
  }> => {
    return api.post(`/billing/invoices/${id}/pay`, data);
  },

  /**
   * Get saved payment methods
   */
  getPaymentMethods: (): Promise<PaymentMethod[]> => {
    return api.get('/billing/payment-methods');
  },

  /**
   * Add new payment method
   */
  addPaymentMethod: (data: AddPaymentMethodRequest): Promise<PaymentMethod> => {
    return api.post('/billing/payment-methods/add', data);
  },

  /**
   * Set default payment method
   */
  setDefaultPaymentMethod: (id: string): Promise<{ success: boolean }> => {
    return api.put(`/billing/payment-methods/${id}/set-default`);
  },

  /**
   * Update billing address for payment method
   */
  updateBillingAddress: (id: string, address: BillingAddress): Promise<PaymentMethod> => {
    return api.put(`/billing/payment-methods/${id}/billing-address`, address);
  },

  /**
   * Remove payment method
   */
  removePaymentMethod: (id: string): Promise<{ success: boolean }> => {
    return api.delete(`/billing/payment-methods/${id}`);
  },

  /**
   * Deduct balance from account
   * Optionally triggers order payment flow when orderId is provided
   */
  deductBalance: (data: {
    amount: number;
    description: string;
    referenceId?: string;
    orderId?: string;
  }): Promise<Balance> => {
    return api.post('/billing/balance/deduct', data);
  },
};
